//* Hooks Imports
import { useCallback, useMemo } from 'react';
import { Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';

//* Components Imports
import { Row, Col, Form } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import '../../../../../../assets/css/typeahead-custom.css';

// * Libraries Imports
import { motion, AnimatePresence } from 'framer-motion';

// * Animations Imports
import { formItemVariants } from '../animations';

// * Schemas Imports
import { CreateResourceQuestion, UpdateResourceQuestion } from '../../../../../../schemas/common/resource/question/resource-question';
import { ProcessExamFiltersResponse } from '../../../../../../schemas/common/process/process-exam-filters';

//* Hooks Imports  
import { useGetProcessExamFilters } from '../../../../../../hooks/queries/process/use-get-process-exam-filters';
import { useParams } from 'react-router-dom';

type ExamSelectionProps = {
  control: Control<CreateResourceQuestion | UpdateResourceQuestion>;
  errors: FieldErrors<CreateResourceQuestion | UpdateResourceQuestion>;
  examFilters?: ProcessExamFiltersResponse['data'];
  setValue: UseFormSetValue<CreateResourceQuestion | UpdateResourceQuestion>;
  disabled?: boolean;
};

type ExamOption = {
  id: number;
  name: string;
  totalQuestions: number;
};

type CategoryOption = {
  id: number;
  name: string;
  questionsCount: number;
};

type QuestionOption = {
  id: number;
  name: string;
  numero: number;
};

export function ExamSelection({
  control,
  errors,
  examFilters,
  setValue,
  disabled = false,
}: ExamSelectionProps) {
  const { id } = useParams<{ id: string }>();
  const processId = id ? parseInt(id) : 0;
  
  // Watch form values for reactive updates
  const watchedProvaId = useWatch({ control, name: "prova_id" });
  const watchedCategoriaId = useWatch({ control, name: "categoria_id" });
  const watchedQuestaoId = useWatch({ control, name: "questao_id" });

  // Query for filtered data when category is selected (to get detailed questions)
  const categoryQuery = useGetProcessExamFilters(processId, { 
    categoria_id: watchedCategoriaId || undefined 
  });

  // Derive exam options from exam filters data
  const examOptions: ExamOption[] = useMemo(() => {
    return examFilters?.provas?.map(prova => ({
      id: prova.id,
      name: prova.name,
      totalQuestions: prova.totalQuestoes,
    })) || [];
  }, [examFilters]);

  const selectedExam = useMemo(() => {
    return examOptions.find(exam => exam.id === watchedProvaId);
  }, [examOptions, watchedProvaId]);

  // Category options from selected exam
  const categoryOptions: CategoryOption[] = useMemo(() => {
    if (!selectedExam || !examFilters) return [];
    
    const prova = examFilters.provas?.find(p => p.id === watchedProvaId);
    return prova?.categorias?.map(categoria => ({
      id: categoria.id,
      name: categoria.name,
      questionsCount: categoria.questoesCount,
    })) || [];
  }, [selectedExam, examFilters, watchedProvaId]);

  const selectedCategory = useMemo(() => {
    return categoryOptions.find(cat => cat.id === watchedCategoriaId);
  }, [categoryOptions, watchedCategoriaId]);
  // Question options from category query (detailed questions)
  const questionOptions: QuestionOption[] = useMemo(() => {
    if (!categoryQuery.data?.success || !categoryQuery.data.data) return [];
    
    return categoryQuery.data.data.provas?.[0]?.categorias?.[0]?.questoes?.map(questao => ({
      id: questao.id,
      name: `Questão ${questao.numero}`, // Display only question number
      numero: questao.numero,
    })) || [];
  }, [categoryQuery.data]);

  const selectedQuestion = useMemo(() => {
    return questionOptions.find(q => q.id === watchedQuestaoId);
  }, [questionOptions, watchedQuestaoId]);
  // Handle exam selection
  const handleExamChange = useCallback((selected: any[]) => {
    const examId = selected[0]?.id || 0;
    setValue("prova_id", examId, { shouldValidate: true });
    setValue("categoria_id", 0, { shouldValidate: true });
    setValue("questao_id", 0, { shouldValidate: true });
  }, [setValue]);

  // Handle category selection
  const handleCategoryChange = useCallback((selected: any[]) => {
    const categoryId = selected[0]?.id || 0;
    setValue("categoria_id", categoryId, { shouldValidate: true });
    setValue("questao_id", 0, { shouldValidate: true });
  }, [setValue]);

  // Handle question selection
  const handleQuestionChange = useCallback((selected: any[]) => {
    const questionId = selected[0]?.id || 0;
    setValue("questao_id", questionId, { shouldValidate: true });
  }, [setValue]);

  return (
    <motion.div variants={formItemVariants} custom={4}>
      <Row className="mb-4">
        {/* Exam Selection */}
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="examSelect">
              Modelo de Prova
              {selectedExam && (
                <span className="ms-2 badge bg-success">
                  <i className="bi bi-check-circle-fill me-1" />
                  Selecionada ({selectedExam.totalQuestions} questões)
                </span>
              )}
            </Form.Label>
            
            <Typeahead
              id="examSelect"
              disabled={disabled}
              options={examOptions}
              labelKey="name"
              placeholder="Digite para buscar um modelo de prova..."
              selected={selectedExam ? [selectedExam] : []}
              onChange={handleExamChange}              
              renderMenuItemChildren={(option: any) => (
                <div>
                  <strong>{option.name}</strong>
                  <div className="text-muted small">
                    {option.totalQuestions} questões
                  </div>
                </div>
              )}
              isInvalid={!!errors.prova_id}
              emptyLabel="Nenhuma prova encontrada"
              clearButton
            />
            
            {errors.prova_id && (
              <Form.Text className="text-danger">
                {errors.prova_id.message || "Por favor, selecione um modelo de prova"}
              </Form.Text>
            )}
          </Form.Group>
        </Col>

        {/* Category Selection */}
        {selectedExam && categoryOptions.length > 0 && (
          <Col md={12}>
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="categorySelect">
                    Disciplina
                    {selectedCategory && (
                      <span className="ms-2 badge bg-success">
                        <i className="bi bi-check-circle-fill me-1" />
                        Selecionada ({selectedCategory.questionsCount} questões)
                      </span>
                    )}
                  </Form.Label>
                  
                  <Typeahead
                    id="categorySelect"
                    disabled={disabled || categoryOptions.length === 0}
                    options={categoryOptions}
                    labelKey="name"
                    placeholder="Digite para buscar uma disciplina..."
                    selected={selectedCategory ? [selectedCategory] : []}
                    onChange={handleCategoryChange}                    
                    renderMenuItemChildren={(option: any) => (
                      <div>
                        <strong>{option.name}</strong>
                        <div className="text-muted small">
                          {option.questionsCount} questões
                        </div>
                      </div>
                    )}
                    isInvalid={!!errors.categoria_id}
                    emptyLabel="Nenhuma disciplina encontrada"
                    clearButton
                  />
                  
                  {errors.categoria_id && (
                    <Form.Text className="text-danger">
                      {errors.categoria_id.message || "Por favor, selecione uma disciplina"}
                    </Form.Text>
                  )}
                </Form.Group>
              </motion.div>
            </AnimatePresence>
          </Col>
        )}

        {/* Question Selection */}
        {selectedCategory && (
          <Col md={12}>
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <Form.Group className="mb-3">                  
                  <Form.Label htmlFor="questionSelect">
                    Questão
                    {selectedQuestion && (
                      <span className="ms-2 badge bg-success">
                        <i className="bi bi-check-circle-fill me-1" />
                        Questão {selectedQuestion.numero} selecionada
                      </span>
                    )}
                  </Form.Label>
                  
                  {categoryQuery.isLoading ? (
                    <div className="d-flex align-items-center">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      Carregando questões...
                    </div>
                  ) : (
                    <Typeahead
                      id="questionSelect"
                      disabled={disabled || questionOptions.length === 0}
                      options={questionOptions}
                      labelKey="name"
                      placeholder="Digite o número da questão..."
                      selected={selectedQuestion ? [selectedQuestion] : []}
                      onChange={handleQuestionChange}                        
                      renderMenuItemChildren={(option: any) => (
                        <div>
                          <strong>Questão {option.numero}</strong>
                        </div>
                      )}
                      isInvalid={!!errors.questao_id}
                      emptyLabel="Nenhuma questão encontrada"
                      clearButton
                    />
                  )}
                  
                  {errors.questao_id && (
                    <Form.Text className="text-danger">
                      {errors.questao_id.message || "Por favor, selecione uma questão"}
                    </Form.Text>
                  )}
                </Form.Group>
              </motion.div>
            </AnimatePresence>
          </Col>
        )}
      </Row>
    </motion.div>
  );
}